import React from "react";
import { Link } from "react-router-dom";
import { Modal } from "components/General/ModalFormComponents";
import CustomInput from "components/General/CustomInput";
import { Alert } from "antd";
import { Button, Input, InputNumber, Spin } from "antd";
import { useSelector } from "react-redux";
import i18n from "i18next";
import { findCorePlatformEntitlement, isCoreTopTier, isProductHeld, shouldShowCorePlanSections } from "util/planEntitlements";
import { hasSonicProspectPlan } from "util/entitlements";
import { useIsEnterpriseCustomer } from "util/platformPricing";
import PlanTierSummary from "./PlanTierSummary";
import PlanVolumeUsage from "./PlanVolumeUsage";
import PlanCreditPools from "./PlanCreditPools";
import PlanCoreQuotas from "./PlanCoreQuotas";
import PlanUsageSection from "./PlanUsageSection";
import PlanAddonMarketplace from "./PlanAddonMarketplace";
import PlanFrontDoorProducts from "./PlanFrontDoorProducts";
import PlanActiveSubscriptions from "./PlanActiveSubscriptions";
import PlanPurchaseHistory from "./PlanPurchaseHistory";
import { resolveRemainingAiCredits } from "./planAiCreditsDisplay";
import styles from "./style.module.scss";

const Wrapper = ({ title, subtitle, children, headerAction }) => (
  <div className={styles.wrapperHeader}>
    <div className={styles.pageTitleRow}>
      <div>
        <h4 className={styles.wrapperTitle}>{title}</h4>
        <p className={styles.wrapperSubtitle}>{subtitle}</p>
      </div>
      {headerAction}
    </div>
    {children}
  </div>
);

const TicketModal = ({
  isTicketModalLoading,
  isTicketModalVisible,
  setIsTicketModalVisible,
  ticketText,
  setTicketText,
  submitTicket,
  theme,
}) => (
  <Modal
    showModal={isTicketModalVisible}
    onClose={() => {
      setIsTicketModalVisible(false);
    }}
    onSave={submitTicket}
    showSaveButton={true}
    showCancelButton={true}
    textonButton={i18n.t("button.4")}
    titleAsset=""
    title={i18n.t("settings.123")}
    widthofmodal="450px"
  >
    <Input.TextArea
      style={{
        height: "130px",
        width: "350px",
        backgroundColor: "transparent",
        borderColor: "#6f6c99 ",
        borderRadius: "10px",
        color: theme === "dark" ? "white" : "black",
      }}
      placeholder={i18n.t("settings.124")}
      onChange={(e) => setTicketText(e.target.value)}
      value={ticketText}
    />
  </Modal>
);

const AiCreditsModal = ({
  open,
  onClose,
  onSave,
  value,
  setValue,
  saving,
}) => (
  <Modal
    showModal={open}
    onClose={onClose}
    onSave={onSave}
    showSaveButton
    showCancelButton
    textonButton={i18n.t("button.4")}
    titleAsset=""
    title={i18n.t("settings.plan_ai_credits_modal_title")}
    widthofmodal="450px"
  >
    <p className={styles.aiCreditsModalHint}>
      {i18n.t("settings.plan_ai_credits_modal_hint")}
    </p>
    <InputNumber
      min={0}
      max={1000000}
      value={value}
      onChange={(next) => setValue(next ?? 0)}
      style={{ width: "100%" }}
      disabled={saving}
    />
    {saving ? <Spin size="small" style={{ marginTop: 12 }} /> : null}
  </Modal>
);

const AppSumo = ({
  show,
  inputLabel,
  inputPlaceholder,
  appSumoCode,
  setAppSumoCode,
  isReedemingAppsumoButton,
  handeAppsumoCoupon,
  handleAppsumoMultipleCoupons,
}) => (
  <>
    {show && (
      <div className={styles.content}>
        <h3 className={styles.header}>{i18n.t("settings.138")}</h3>
        <div className={styles.row}>
          <div>
            <CustomInput
              label={inputLabel}
              placeholder={inputPlaceholder}
              value={appSumoCode}
              onChange={(val) => setAppSumoCode(val)}
              style={{ margin: "0px" }}
            />
          </div>
        </div>
        <div className={styles.rowBtns}>
          <div className={styles.btn}>
            <Button
              disabled={isReedemingAppsumoButton}
              onClick={() => handeAppsumoCoupon()}
            >
              {i18n.t("settings.139")}
            </Button>
          </div>
        </div>
      </div>
    )}
  </>
);

const getInputLabel = (packageId) => {
  switch (packageId) {
    case 65:
      return i18n.t("settings.160");
    case 66:
      return i18n.t("settings.161");
    case 68:
      return i18n.t("settings.162");
    case 69:
      return i18n.t("settings.163");
    default:
      return "";
  }
};

const Representation = ({
  plan,
  entitlementsMe,
  entitlementsLoading,
  entitlementsError,
  alertLimits,
  alertLimitsLoading,
  canManagePurchases,
  purchaseHistory,
  purchaseHistoryTotal,
  purchaseHistoryLoading,
  onRefreshPurchaseHistory,
  appSumoCode,
  setAppSumoCode,
  handeAppsumoCoupon,
  handleAppsumoMultipleCoupons,
  isReedemingAppsumoButton,
  isTicketModalLoading,
  isTicketModalVisible,
  setIsTicketModalVisible,
  ticketText,
  setTicketText,
  submitTicket,
  onOpenContactRequest,
  canUpdateAiCredits,
  aiCreditsModalOpen,
  setAiCreditsModalOpen,
  aiCreditsDraft,
  setAiCreditsDraft,
  onSaveAiCredits,
  aiCreditsSaving,
  user,
}) => {
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const isEnterprise = useIsEnterpriseCustomer();
  const entitlements = entitlementsMe?.entitlements || [];
  const products = entitlementsMe?.products || [];
  const creditPools = entitlementsMe?.credit_pools || [];
  const coreEntitlement = findCorePlatformEntitlement(entitlements);
  const showCoreSections = shouldShowCorePlanSections(coreEntitlement);
  const showProspectBilling = hasSonicProspectPlan(plan);
  const isTopTier = isCoreTopTier(coreEntitlement);
  const showMiDiscoveryLink =
    showCoreSections &&
    !isProductHeld("market_intelligence", entitlements) &&
    !isProductHeld("full_intelligence", entitlements);
  const quotaRenewalHint =
    showCoreSections && plan?.QuotaRenewalAnchorDay != null
      ? i18n.t("settings.plan_quota_renewal_usage", {
          day: plan.QuotaRenewalAnchorDay,
        })
      : null;
  const quotaCapacityHint = showCoreSections
    ? i18n.t("settings.plan_quota_capacity_note")
    : null;

  const openHeaderContact = () => {
    onOpenContactRequest({
      productKey: isEnterprise || isTopTier ? "plan_capacity" : "plan_upgrade",
      productName:
        isEnterprise || isTopTier
          ? i18n.t("settings.plan_contact_request")
          : i18n.t("settings.plan_upgrade_request"),
    });
  };

  return (
    <Wrapper
      title={i18n.t("settings.plan_page_title")}
      subtitle={i18n.t("settings.156")}
      headerAction={
        <div className={`${styles.btn} ${styles.btnHeader}`}>
          <Button onClick={openHeaderContact}>
            {isEnterprise || isTopTier
              ? i18n.t("settings.plan_contact_cta")
              : i18n.t("settings.plan_upgrade_cta")}
          </Button>
        </div>
      }
    >
      <div className={styles.wrapper}>
        {entitlementsError ? (
          <Alert
            type="warning"
            showIcon
            message={entitlementsError}
            className={styles.planAlert}
          />
        ) : null}

        {/* Paid core plan first; trial Sonic subscriptions follow below. */}
        {showCoreSections || entitlementsLoading ? (
          <PlanTierSummary
            coreEntitlement={coreEntitlement}
            renewalDate={plan?.NextQuotaRenewalDate}
            quotaRenewalHint={quotaRenewalHint}
            quotaCapacityHint={quotaCapacityHint}
            loading={entitlementsLoading}
          />
        ) : null}

        {showMiDiscoveryLink ? (
          <p className={styles.miDiscoveryLink}>
            <Link to="/console/industryTopics">
              {i18n.t("settings.plan_mi_discovery_link")}
            </Link>
          </p>
        ) : null}

        {showCoreSections ? (
          <PlanUsageSection>
            <PlanVolumeUsage
              coreEntitlement={coreEntitlement}
              loading={entitlementsLoading}
            />
            <PlanCreditPools
              creditPools={creditPools}
              products={products}
              loading={entitlementsLoading}
            />
            <PlanCoreQuotas
              plan={plan}
              alertLimits={alertLimits}
              alertLimitsLoading={alertLimitsLoading}
              canManagePurchases={canManagePurchases}
              canUpdateAiCredits={canUpdateAiCredits}
              onOpenAiCreditsModal={() => {
                setAiCreditsDraft(resolveRemainingAiCredits(plan) ?? 0);
                setAiCreditsModalOpen(true);
              }}
              aiCreditsSaving={aiCreditsSaving}
              isEnterprise={isEnterprise}
              onContactSales={() =>
                onOpenContactRequest({
                  productKey: "org_quota_addon",
                  productName: i18n.t("settings.plan_org_addon_subscribe", {
                    pack: 10,
                    defaultValue: "Organization quota add-on",
                  }),
                })
              }
            />
          </PlanUsageSection>
        ) : null}

        <PlanActiveSubscriptions
          entitlements={entitlements}
          loading={entitlementsLoading}
          isEnterprise={isEnterprise}
          onRequestProduct={onOpenContactRequest}
          canManagePurchases={canManagePurchases}
          user={user}
          showProspectBilling={showProspectBilling}
        />

        <PlanAddonMarketplace
          products={products}
          entitlements={entitlements}
          onRequestProduct={onOpenContactRequest}
          loading={entitlementsLoading}
          isEnterprise={isEnterprise}
        />

        <PlanFrontDoorProducts
          products={products}
          entitlements={entitlements}
          onRequestProduct={onOpenContactRequest}
          loading={entitlementsLoading}
          isEnterprise={isEnterprise}
        />

        <div className={styles.content}>
          <div className={styles.rowBtns}>
            <div className={`${styles.btn} ${styles.btnContact}`}>
              <button
                type="button"
                onClick={() =>
                  onOpenContactRequest({
                    productKey: "support",
                    productName: i18n.t("settings.137"),
                  })
                }
              >
                {i18n.t("settings.137")}
              </button>
            </div>
          </div>
        </div>

        <PlanPurchaseHistory
          purchases={purchaseHistory}
          total={purchaseHistoryTotal}
          loading={purchaseHistoryLoading}
          onRefresh={onRefreshPurchaseHistory}
          canView={canManagePurchases}
        />

        <AppSumo
          appSumoCode={appSumoCode}
          setAppSumoCode={setAppSumoCode}
          inputLabel={getInputLabel(plan?.PackageId)}
          inputPlaceholder={
            plan?.PackageId === 65 || plan?.PackageId === 68
              ? i18n.t("settings.157")
              : i18n.t("settings.158")
          }
          show={
            plan?.PackageId === 65 ||
            plan?.PackageId === 66 ||
            plan?.PackageId === 69
          }
        />
        {plan?.PackageId === 68 && (
          <div className={styles.content}>
            <h4 className={styles.header}>{i18n.t("settings.159")}</h4>
            <div className={styles.row}>
              <CustomInput
                placeholder="COUPON1,COUPON2,COUPON3"
                value={appSumoCode}
                onChange={(val) => setAppSumoCode(val)}
                style={{ margin: "0px" }}
              />
            </div>
            <div className={styles.rowBtns}>
              <div className={styles.btn}>
                <Button
                  disabled={isReedemingAppsumoButton}
                  onClick={() => handleAppsumoMultipleCoupons()}
                >
                  {i18n.t("button.36")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <TicketModal
        isTicketModalVisible={isTicketModalVisible}
        submitTicket={submitTicket}
        isTicketModalLoading={isTicketModalLoading}
        ticketText={ticketText}
        setTicketText={setTicketText}
        setIsTicketModalVisible={setIsTicketModalVisible}
        theme={theme}
      />
      <AiCreditsModal
        open={aiCreditsModalOpen}
        onClose={() => setAiCreditsModalOpen(false)}
        onSave={onSaveAiCredits}
        value={aiCreditsDraft}
        setValue={setAiCreditsDraft}
        saving={aiCreditsSaving}
      />
    </Wrapper>
  );
};

export default Representation;
